import { Alert, Box, Card, CardContent, Tab, Tabs, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HttpError, Title, useNotify } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { createPunches, declinePunches, loadDayData, recalculate } from '../../../lib/devTools/api';
import { isDevToolsPunchesEnabled } from '../../../lib/config';
import { DevToolsGate } from '../DevToolsGate';
import { AddCustomDialog } from './Data/AddCustomDialog';
import { AddFromScheduleDialog } from './Data/AddFromScheduleDialog';
import { AddSinglePunchDialog } from './Data/AddSinglePunchDialog';
import { DataToolbar } from './Data/DataToolbar';
import { DayDataTable } from './Data/DayDataTable';
import { EntitySummary } from './EntitySummary';
import { JsonView } from './JsonView';
import { PipelineView } from './PipelineView';
import { PlanProvenanceView } from './PlanProvenance';
import { RecalculateForm } from './RecalculateForm';
import { ReportView } from './ReportView';
import { CreatePunchesRequest, CreatePunchesResponse, DayDataResponse, RecalculateResponse } from './types';
import { RecalculateParams, isComplete, parseParams, serializeParams } from './urlParams';

/** Server-side cap on uuids per decline-punches request. */
const DECLINE_CHUNK_SIZE = 500;

/** Builds the single "Declined N punches[, ...]" notification shared by handleDeclinePunch and
 * handleDeclineAll (the latter after summing chunked responses), and whether it should show as a
 * warning (day-punching or Rails itself failed on at least one punch) or a plain success.
 * railsSkipped alone (no Rails mapping to begin with) is expected behaviour, not a failure. */
const declineNotification = (result: { declined: number; failed: string[]; railsSkipped: string[]; railsFailed: string[] }) => {
  let message = `Declined ${result.declined} punches`;
  if (result.failed.length) message += `, ${result.failed.length} failed`;
  if (result.railsSkipped.length) message += `, ${result.railsSkipped.length} without a Rails punch`;
  if (result.railsFailed.length) message += `, ${result.railsFailed.length} Rails decline failed`;
  return { message, type: (result.failed.length || result.railsFailed.length ? 'warning' : 'success') as 'warning' | 'success' };
};

export const RecalculateTool = () => {
  const punchesEnabled = isDevToolsPunchesEnabled();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => parseParams(searchParams, new Date()), [searchParams]);
  const [result, setResult] = useState<{ params: RecalculateParams; response: RecalculateResponse } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<'data' | 'report' | 'pipeline' | 'json'>('data');
  const notify = useNotify();
  const autoRan = useRef(false);

  // dayData is tied to the params it was loaded WITH (not the live, possibly-since-edited form
  // params) — every Data-tab action (create, decline, decline all, and the AddCustomDialog's default
  // range) must act against the company/user/range the table is actually showing.
  const [dayData, setDayData] = useState<{ params: RecalculateParams; response: DayDataResponse } | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [addFromScheduleOpen, setAddFromScheduleOpen] = useState(false);
  const [addCustomOpen, setAddCustomOpen] = useState(false);
  const [addSingleDate, setAddSingleDate] = useState<string | null>(null);

  // True once the live form params have drifted from the params the loaded data reflects — the
  // developer edited a field after Load data ran. Data actions are disabled until Load data is
  // pressed again, so a create/decline never targets a range the table isn't actually showing.
  const stale = !!dayData && serializeParams(params).toString() !== serializeParams(dayData.params).toString();

  const run = useCallback(
    async (p: RecalculateParams) => {
      setLoading(true);
      setError(null);
      try {
        const response = await recalculate(p.companyUuid, p.userProfileUuid, { from: p.from, to: p.to });
        setResult({ params: p, response });
      } catch (e) {
        const message = e instanceof HttpError || e instanceof Error ? e.message : 'Recalculation failed';
        setError(message);
        notify(message, { type: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [notify]
  );

  const loadData = useCallback(
    async (p: RecalculateParams) => {
      setLoadingData(true);
      try {
        const response = await loadDayData(p.companyUuid, p.userProfileUuid, { from: p.from, to: p.to });
        setDayData({ params: p, response });
        setDataError(null);
      } catch (e) {
        const message = e instanceof HttpError || e instanceof Error ? e.message : 'Failed to load day data';
        setDataError(message);
        notify(message, { type: 'error' });
        // dayData itself is left untouched — the Data tab keeps whatever it last showed.
      } finally {
        setLoadingData(false);
      }
    },
    [notify]
  );

  // A deep link with every param present (e.g. from the Profile page) runs Run and Load data once, in parallel, only when the page OPENS with all four valid params.
  useEffect(() => {
    if (autoRan.current) return;
    autoRan.current = true;
    if (isComplete(params)) {
      setSearchParams(serializeParams(params), { replace: true });
      run(params);
      loadData(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitPunches = useCallback(
    async (request: CreatePunchesRequest): Promise<CreatePunchesResponse> => {
      if (!dayData) throw new Error('No data loaded');
      try {
        const response = await createPunches(dayData.params.companyUuid, dayData.params.userProfileUuid, request);
        notify(`Created ${response.created.length}, failed ${response.failed.length}${response.failed[0] ? `: ${response.failed[0].error}` : ''}`, { type: response.failed.length ? 'warning' : 'success' });
        await loadData(dayData.params);
        return response;
      } catch (e) {
        const message = e instanceof HttpError || e instanceof Error ? e.message : 'Failed to create punches';
        notify(message, { type: 'error' });
        throw e;
      }
    },
    [dayData, notify, loadData]
  );

  const handleDeclinePunch = useCallback(
    async (uuid: string) => {
      if (!dayData) return;
      try {
        const response = await declinePunches(dayData.params.companyUuid, dayData.params.userProfileUuid, [uuid]);
        const { message, type } = declineNotification(response);
        notify(message, { type });
        await loadData(dayData.params);
      } catch (e) {
        const message = e instanceof HttpError || e instanceof Error ? e.message : 'Failed to decline punch';
        notify(message, { type: 'error' });
      }
    },
    [dayData, notify, loadData]
  );

  const handleDeclineAll = useCallback(async () => {
    if (!dayData) return;
    const uuids = dayData.response.days.flatMap((d) => d.punches.map((p) => p.uuid));
    if (uuids.length === 0) return;
    try {
      // The server caps decline requests at DECLINE_CHUNK_SIZE uuids each — "Decline all in range"
      // can exceed that, so chunks are sent sequentially and their results summed for one notification.
      let declined = 0;
      const failed: string[] = [];
      const railsSkipped: string[] = [];
      const railsFailed: string[] = [];
      for (let i = 0; i < uuids.length; i += DECLINE_CHUNK_SIZE) {
        const response = await declinePunches(dayData.params.companyUuid, dayData.params.userProfileUuid, uuids.slice(i, i + DECLINE_CHUNK_SIZE));
        declined += response.declined;
        failed.push(...response.failed);
        railsSkipped.push(...response.railsSkipped);
        railsFailed.push(...response.railsFailed);
      }
      const { message, type } = declineNotification({ declined, failed, railsSkipped, railsFailed });
      notify(message, { type });
      await loadData(dayData.params);
    } catch (e) {
      const message = e instanceof HttpError || e instanceof Error ? e.message : 'Failed to decline punches';
      notify(message, { type: 'error' });
    }
  }, [dayData, notify, loadData]);

  return (
    <Box sx={{ mt: 2 }}>
      <Title title="Dev Tools · Recalculate" />
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recalculate
          </Typography>
          <RecalculateForm value={params} onChange={(next) => setSearchParams(serializeParams(next), { replace: true })} onSubmit={() => run(params)} onLoadData={() => loadData(params)} loading={loading} loadingData={loadingData} />
          <EntitySummary companyUuid={params.companyUuid} userProfileUuid={params.userProfileUuid} />
        </CardContent>
      </Card>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab value="data" label="Data" />
        <Tab value="report" label="Report" />
        <Tab value="pipeline" label="Pipeline" />
        <Tab value="json" label="JSON" />
      </Tabs>
      {/* The provenance card is not Pipeline-specific — it shows above whichever of Report/Pipeline/JSON is active, once a result exists. It never shows on the Data tab, which has its own header (DayDataTable's time zone/counts line). */}
      {result && tab !== 'data' && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <PlanProvenanceView metadata={result.response.metadata} />
          </CardContent>
        </Card>
      )}
      {tab === 'data' && (
        <Box>
          {dataError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dataError}
            </Alert>
          )}
          {dayData ? (
            <Box>
              {stale && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Parameters changed. Press Load data to refresh.
                </Alert>
              )}
              {!punchesEnabled && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Creating and declining punches is switched off in this environment. You can still load and read the data.
                </Alert>
              )}
              {punchesEnabled && <DataToolbar disabled={stale} onAddFromSchedule={() => setAddFromScheduleOpen(true)} onAddCustom={() => setAddCustomOpen(true)} onDeclineAll={handleDeclineAll} />}
              <DayDataTable dayData={dayData.response} disabled={stale} readOnly={!punchesEnabled} onAddPunch={setAddSingleDate} onDeclinePunch={handleDeclinePunch} />
              {punchesEnabled && addFromScheduleOpen && <AddFromScheduleDialog open days={dayData.response.days} context={dayData.response.context} onClose={() => setAddFromScheduleOpen(false)} onCreate={submitPunches} />}
              {punchesEnabled && addCustomOpen && <AddCustomDialog open range={{ from: dayData.params.from, to: dayData.params.to }} days={dayData.response.days} context={dayData.response.context} onClose={() => setAddCustomOpen(false)} onCreate={submitPunches} />}
              {punchesEnabled && addSingleDate && (
                <AddSinglePunchDialog
                  open
                  date={addSingleDate}
                  context={dayData.response.context}
                  onClose={() => setAddSingleDate(null)}
                  onCreate={(punch) => submitPunches({ createdBy: punch.createdBy, requestedBy: punch.requestedBy, reason: punch.reason || undefined, punches: [{ date: punch.date, time: punch.time, punchType: punch.punchType }] })}
                />
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">Press Load data</Typography>
          )}
        </Box>
      )}
      {tab === 'report' && (result ? <ReportView response={result.response} range={{ from: result.params.from, to: result.params.to }} /> : <Typography color="text.secondary">Press Run</Typography>)}
      {tab === 'pipeline' && (result ? <PipelineView response={result.response} range={{ from: result.params.from, to: result.params.to }} /> : <Typography color="text.secondary">Press Run</Typography>)}
      {tab === 'json' && (result ? <JsonView value={result.response} /> : <Typography color="text.secondary">Press Run</Typography>)}
    </Box>
  );
};

export const RecalculatePage = () => (
  <DevToolsGate>
    <RecalculateTool />
  </DevToolsGate>
);

export default RecalculatePage;
